import { all, call, CallEffect, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { fetchDraftsRequested } from '~src/drafts/actions/DraftsActions';
import { fetchUnpublishedWorkbookDraftCount } from '~src/drafts/sagas/DraftsSagas';
import { DraftUtils } from '~src/drafts/utils/DraftUtils';
import { GeocodingPermissionHelper } from '~src/geocoding/utils/GeocodingPermissionHelper';
import { IsNlpEnabled } from '~src/lens/utils/LensUtil';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { selectContentCountsUpToDate } from '~src/spacesPlatform/selectors/ContentCountsSelector';
import { selectMixedContentFilterClauses } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import {
  DataRoleSubspaceName,
  DatasourceSubspaceName,
  isCatalogSpace,
  isDataRoleSpace,
  isDatasourceSpace,
  isFavoritesSpace,
  isPersonalSpace,
  isProjectSpace,
  isPublishedConnectionSpace,
  isPublishedConnectionTableSpace,
  isScheduleSpace,
  isSearchSpace,
  isServerTasksSpace,
  isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteSpace,
  isSiteTasksSpace,
  isSiteUserSpace,
  isWorkbookSpace,
  PublishedConnectionSubspaceName,
  PublishedConnectionTableSubspaceName,
  ScheduleSubspaceName,
  SearchSubspaceName,
  ServerTasksSubspaceName,
  SiteTasksSubspaceName,
  SiteUserSubspaceName,
  SpaceInfo,
  SubspaceName,
  WorkbookSubspaceName,
} from '~src/spacesPlatform/state/SpaceInfo';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { selectCatalogingOnSiteEnabled } from '../../../catalog/selectors/CatalogSelectors';
import { Messages } from '../../../codegen/Localize';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { IResourceQuery } from '../../../httpClient/resources/ResourceQuery';
import { ContentCountsRequestAction, contentCountsRequestSucceeded, } from '../../actions/ContentCountsActions';
import { IStringValuedFilterClause } from '../../models/IFilterClause';
import { SpaceInfoTypeContentCount, SpaceInfoTypeContentCounts, } from '../../state/ContentCountsState';
import { FilterMap } from '../../state/ContentExplorerState';
import { SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import {
  getCollectionItemsResourceQuery,
  getFavoritesResourceQueryForContentTypes,
  getPersonalSpaceResourceQueryForContentTypes,
  getProjectOrSiteResourceQueryForContentTypes,
  getResourceQuery,
  getSharedWithMeResourceQuery,
  getSiteResourceQueryForCatalogSpaceContentType,
  getSiteResourceQueryForSiteSpaceContentType,
  getSiteUser_ContentSubspace_ResourceQueryForContentTypes,
  getSiteUser_ResourceQueryForSpaceTypes,
} from './MixedContentSagas';

export function* processContentCountRequest(
  resource: IResourceQuery<any>
): any {
  try {
    if (_.isUndefined(resource)) {
      return 0;
    }
    resource = resource.withOrder([]); // ignore any sort order

    const count: number = yield call(fetchCountForContent, resource);
    return count;
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// For the counts, all we need is the "totalCount", so just request zero items
export function fetchCountForContent<T>(
  resource: IResourceQuery<T>
): Promise<number> | undefined {
  return resource.getCount();
}

export function* processContentCountRequestForFavorites(
  contentType: ExploreContentApi_ContentType
) {
  const resource = yield call(getFavoritesResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForProject(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getProjectOrSiteResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSharedWithMe(
  contentType: ExploreContentApi_ContentType
) {
  // In SWM space, we need count for each content type specifically so adding filter clause to the request
  // TODO: This needs to be fixed.
  // We would need to change the pattern here because all other spaces, e.g. favourites, explore, push out
  // multiple requests like getProject, getViews, getWorkbook when you open the contenttype filter
  // In SwM case, we need to push multiple getSharedWithMe requests with filter value updated
  // TFS 1107593
  const resource = yield call(getSharedWithMeResourceQuery, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSiteUserCollections(
  spaceInfo: SpaceInfo
) {
  const resource = yield call(
    getSiteUser_ResourceQueryForSpaceTypes,
    spaceInfo
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSiteUserContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(
    getSiteUser_ContentSubspace_ResourceQueryForContentTypes,
    [contentType]
  );
  return yield call(processContentCountRequest, resource);
}

export function* processWorkbookCountRequestWithUnpublishedDrafts(spaceInfo: SpaceInfo): any {
  const counts: number[] = yield all([
    isSiteSpace(spaceInfo)
      ? call(processContentCountRequestForSite, 'workbook')
      : call(zero),
    isProjectSpace(spaceInfo)
      ? call(processContentCountRequestForProject, 'workbook')
      : call(zero),
    isSiteUserSpace(spaceInfo)
      ? call(processContentCountRequestForSiteUserContentType, 'workbook')
      : call(zero),
    call(processUnpublishedWorkbookDraftCountRequest, spaceInfo),
  ]);

  return counts.reduce((sum, value) => sum + value, 0);
}

export function* processUnpublishedWorkbookDraftCountRequest(spaceInfo: SpaceInfo) {
  const contentFilters: FilterMap = yield select(selectMixedContentFilterClauses);
  if (!DraftUtils.shouldIncludeUnpublishedWorkbookDraftCount(contentFilters, spaceInfo)) {
    return 0;
  }

  const action = fetchDraftsRequested(spaceInfo, contentFilters);
  const count: number =  yield call(fetchUnpublishedWorkbookDraftCount, action);
  return count;
}

export function* processContentCountRequestForPersonalSpaceContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getPersonalSpaceResourceQueryForContentTypes, [
    contentType,
  ]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForCollectionSpaceContentType(
  contentType: ExploreContentApi_ContentType
) {
  // The counts for the content types need to look specifically at each content type,
  const resource = yield call(getCollectionItemsResourceQuery, [contentType]);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSite(
  siteSpaceContentType: SiteSpaceContentType
) {
  // The counts for the content types need to look specifically at each site content type,
  const resource = yield call(
    getSiteResourceQueryForSiteSpaceContentType,
    siteSpaceContentType
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForCatalog(
  siteSpaceContentType: SiteSpaceContentType
) {
  const resource = yield call(
    getSiteResourceQueryForCatalogSpaceContentType,
    siteSpaceContentType
  );
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSpaceInfo(spaceInfo: SpaceInfo) {
  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* processContentCountRequestForSearchSpaceInfo(
  spaceInfo: SpaceInfo
) {
  // if the current search term in empty, return 0
  const mixedContentFilter: FilterMap = yield select(
    selectMixedContentFilterClauses
  );
  if (
    _.isEmpty(mixedContentFilter) ||
    !mixedContentFilter.search ||
    ((mixedContentFilter.search as IStringValuedFilterClause).value.length === 0 && !FeatureFlags.searchQuickLinks))
    {
    return 0;
  }

  const resource = yield call(getResourceQuery, spaceInfo);
  return yield call(processContentCountRequest, resource);
}

export function* handleContentCountsRequest(
  action: ContentCountsRequestAction
) {
  try {
    const isUpToDate = yield select(selectContentCountsUpToDate);
    if (!isUpToDate) {
      const flowsEnabled: boolean = yield select(selectFlowsOnSiteEnabled);
      const catalogingOnSiteEnabled: boolean = yield select(
        selectCatalogingOnSiteEnabled
      );
      const areUsersVisible: boolean = !(yield select(
        CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced
      ));
      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      const counts: SpaceInfoTypeContentCounts = yield all(
        getContentCountsRequests(
          spaceInfo,
          flowsEnabled,
          catalogingOnSiteEnabled,
          areUsersVisible
        )
      );
      yield put(contentCountsRequestSucceeded(spaceInfo.spaceType, counts));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// exported for testing
export function getContentCountsRequests(
  spaceInfo: SpaceInfo,
  flowsEnabled: boolean,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean
): Partial<Record<ExploreContentApi_ContentType | SubspaceName, CallEffect>> {
  const pConnsEnabled: boolean =
    FeatureFlags.publishedConnectionContentManagementEnabled;
  const predictiveModelEnabled: boolean = false;
  if (isSiteSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<SiteSpaceContentType, CallEffect> =
      {
        explore: call(processContentCountRequestForSite, 'explore'),
        project: call(processContentCountRequestForSite, 'project'),
        workbook: call(processWorkbookCountRequestWithUnpublishedDrafts, spaceInfo),
        datasource: call(processContentCountRequestForSite, 'datasource'),
        dataRole: IsNlpEnabled()
          ? call(processContentCountRequestForSite, 'dataRole')
          : call(zero),
        lens: IsNlpEnabled()
          ? call(processContentCountRequestForSite, 'lens')
          : call(zero),
        view: call(processContentCountRequestForSite, 'view'),
        flow: flowsEnabled
          ? call(processContentCountRequestForSite, 'flow')
          : call(zero),
        dataconnection: call(zero),
        database: call(zero),
        table: call(zero),
        collection: call(zero),
        virtualConnection: pConnsEnabled
          ? call(processContentCountRequestForSite, 'virtualConnection')
          : call(zero),
        virtualConnectionTable: call(zero),
        virtualConnectionColumn: call(zero),
        predictiveModel: predictiveModelEnabled
          ? call(processContentCountRequestForSite, 'predictiveModel')
          : call(zero)
      };
    return counts;
  } else if (isProjectSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(processContentCountRequestForProject, 'project'),
      workbook: call(processWorkbookCountRequestWithUnpublishedDrafts, spaceInfo),
      datasource: call(processContentCountRequestForProject, 'datasource'),
      dataRole: IsNlpEnabled()
        ? call(processContentCountRequestForProject, 'dataRole')
        : call(zero),
      lens: IsNlpEnabled()
        ? call(processContentCountRequestForProject, 'lens')
        : call(zero),
      view: call(processContentCountRequestForProject, 'view'),
      flow: flowsEnabled
        ? call(processContentCountRequestForProject, 'flow')
        : call(zero),
      dataconnection: call(zero),
      database: (FeatureFlags.databasesInProjectsEnabled &&
        FeatureFlags.databasesInProjectsPostMVPEnabled && catalogingOnSiteEnabled) ?
        call(processContentCountRequestForProject, 'database') :
        call(zero),
      table: (FeatureFlags.tablesInProjectsEnabled &&
        FeatureFlags.tablesInProjectsUIEnabled && catalogingOnSiteEnabled) ?
        call(processContentCountRequestForProject, 'table') :
        call(zero),
      collection: call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForProject, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForProject, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isFavoritesSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(processContentCountRequestForFavorites, 'project'),
      workbook: call(processContentCountRequestForFavorites, 'workbook'),
      datasource: call(processContentCountRequestForFavorites, 'datasource'),
      dataRole: IsNlpEnabled()
        ? call(processContentCountRequestForFavorites, 'dataRole')
        : call(zero),
      lens: IsNlpEnabled()
        ? call(processContentCountRequestForFavorites, 'lens')
        : call(zero),
      view: call(processContentCountRequestForFavorites, 'view'),
      flow: flowsEnabled
        ? call(processContentCountRequestForFavorites, 'flow')
        : call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForFavorites, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: pConnsEnabled
        ? call(processContentCountRequestForFavorites, 'virtualConnectionTable')
        : call(zero),
      virtualConnectionColumn: call(zero),
      collection: CollectionUtils.checkCollectionSiteSetting()
        ? call(processContentCountRequestForFavorites, 'collection')
        : call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForFavorites, 'predictiveModel')
        : call(zero)
    };
    return counts;
  } else if (isWorkbookSpace(spaceInfo)) {
    const counts: Record<WorkbookSubspaceName, CallEffect> = {
      datasources: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'datasources',
      }),
      views: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'views',
      }),
      customViews: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'customViews',
      }),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      dataconnections: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'dataconnections',
      }),
      extractTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractTasks',
      }),
      lineage: call(zero),
      usage: call(zero),
    };
    return counts;
  } else if (isDatasourceSpace(spaceInfo)) {
    const counts: Record<DatasourceSubspaceName, CallEffect> = {
      lineage: call(zero),
      nlp: call(zero),
      connections: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connections',
      }),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractRefreshes',
      }),
      remoteRefreshSchedules: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'remoteRefreshSchedules',
      }),
      connectedWorkbooks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedWorkbooks',
      }),
      askDataUsageAnalytics: call(zero),
    };
    return counts;
  } else if (isSearchSpace(spaceInfo)) {
    const counts: Record<SearchSubspaceName, CallEffect> = {
      views: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'views',
      }),
      workbooks: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'workbooks',
      }),
      datasources: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'datasources',
      }),
      flows: flowsEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'flows',
        })
        : call(zero),
      projects: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'projects',
      }),
      tables: call(zero),
      databases: call(zero),
      tablesWithCrass: catalogingOnSiteEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'tablesWithCrass',
        })
        : call(zero),
      databasesWithCrass: catalogingOnSiteEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'databasesWithCrass',
        })
        : call(zero),
      dataRoles: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'dataRoles',
      }),
      lenses: call(processContentCountRequestForSearchSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lenses',
      }),
      users: areUsersVisible
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'users',
        })
        : call(zero),
      virtualConnections: pConnsEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'virtualConnections',
        })
        : call(zero),
      predictiveModels: predictiveModelEnabled
        ? call(processContentCountRequestForSearchSpaceInfo, {
          ...spaceInfo,
          subspaceName: 'predictiveModels',
        })
        : call(zero),
    };
    return counts;
  } else if (isSiteUserSpace(spaceInfo)) {
    const counts: Record<
      SiteUserSubspaceName | ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(
        processContentCountRequestForSiteUserContentType,
        'project'
      ),
      workbook: call(processWorkbookCountRequestWithUnpublishedDrafts, spaceInfo),
      view: call(processContentCountRequestForSiteUserContentType, 'view'),
      datasource: call(
        processContentCountRequestForSiteUserContentType,
        'datasource'
      ),
      flow: flowsEnabled
        ? call(processContentCountRequestForSiteUserContentType, 'flow')
        : call(zero),
      dataRole: call(
        processContentCountRequestForSiteUserContentType,
        'dataRole'
      ),
      lens: call(processContentCountRequestForSiteUserContentType, 'lens'),
      virtualConnection: pConnsEnabled
        ? call(
          processContentCountRequestForSiteUserContentType,
          'virtualConnection'
        )
        : call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      content: call(zero),
      collection: call(zero),
      alerts: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'alerts',
      }),
      collections: CollectionUtils.checkCollectionSiteSetting() ? call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'collections',
      }) : call(zero),
      customViews: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'customViews',
      }),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      performance: FeatureFlags.viewAccelerationRecommendationEngineEnabled ? call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'performance',
      }) : call(zero),
      settings: call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForSiteUserContentType, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isDataRoleSpace(spaceInfo)) {
    const counts: Record<DataRoleSubspaceName, CallEffect> = {
      definition: call(zero),
      connectedFlows: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedFlows',
      }),
      connectedDatasources: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'connectedDatasources',
      }),
    };
    return counts;
  } else if (isScheduleSpace(spaceInfo)) {
    const counts: Record<ScheduleSubspaceName, CallEffect> = {
      details: call(zero),
      subscriptions: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'subscriptions',
      }),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'extractRefreshes',
      }),
      flows: flowsEnabled
        ? call(processContentCountRequestForSpaceInfo, {
            ...spaceInfo,
            subspaceName: 'flows',
          })
        : call(zero),
    };
    return counts;
  } else if (isSiteTasksSpace(spaceInfo)) {
      const counts: Record<SiteTasksSubspaceName, CallEffect> = {
        dataAcceleration: FeatureFlags.accelerationSiteAdminUIEnabled
          ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataAcceleration'}) : call(zero),
        extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
        runFlowTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'runFlowTasks'}),
        subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
        alerts: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'alerts'}),
        linkedTasks: areLinkedTasksEnabled() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'linkedTasks'}): call(zero),
        addressGeocodingTasks: GeocodingPermissionHelper.isGeocodingEnabledOnSite() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'addressGeocodingTasks'}) : call(zero),
      };
      return counts;
  } else if (isServerTasksSpace(spaceInfo)) {
    const counts: Record<ServerTasksSubspaceName, CallEffect> = {
      dataAcceleration: FeatureFlags.accelerationSiteAdminUIEnabled
        ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'dataAcceleration'}) : call(zero),
      extractRefreshes: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'extractRefreshes'}),
      runFlowTasks: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'runFlowTasks'}),
      subscriptions: call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'subscriptions'}),
      linkedTasks: areLinkedTasksEnabled() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'linkedTasks'}): call(zero),
      addressGeocodingTasks: GeocodingPermissionHelper.isGeocodingEnabledOnSite() ? call(processContentCountRequestForSpaceInfo, {...spaceInfo, subspaceName: 'addressGeocodingTasks'}) : call(zero),
    };
    return counts;
  } else if (isSharedWithMeSpace(spaceInfo)) {
    // const counts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType, CallEffect> = {
    //   project: call(processContentCountRequestForSharedWithMe, 'project'),
    //   workbook: call(processContentCountRequestForSharedWithMe, 'workbook'),
    //   datasource: call(processContentCountRequestForSharedWithMe, 'datasource'),
    //   dataRole: call(zero),
    //   view: call(processContentCountRequestForSharedWithMe, 'view'),
    //   flow: flowsEnabled ? call(processContentCountRequestForSharedWithMe, 'flow') : call(zero),
    //   dataconnection: call(zero),
    //   database: call(zero),
    //   table: call(zero),
    //   metric: isMetricsEnabled() ? call(processContentCountRequestForSharedWithMe, 'metric') : call(zero),
    //   virtualConnection: call(zero),
    //   collection: call(zero),
    // };
    // TFSID 1119981 Temporarily disable count calculation logic.
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(zero),
      datasource: call(zero),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: call(zero),
    };
    return counts;
  } else if (isPersonalSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(
        processContentCountRequestForPersonalSpaceContentType,
        'workbook'
      ),
      datasource: call(zero),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForPersonalSpaceContentType, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isSingleCollectionSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(
        processContentCountRequestForCollectionSpaceContentType,
        'project'
      ),
      workbook: call(
        processContentCountRequestForCollectionSpaceContentType,
        'workbook'
      ),
      datasource: call(
        processContentCountRequestForCollectionSpaceContentType,
        'datasource'
      ),
      dataRole: IsNlpEnabled()
        ? call(processContentCountRequestForCollectionSpaceContentType, 'dataRole')
        : call(zero),
      lens: call(zero),
      view: call(
        processContentCountRequestForCollectionSpaceContentType,
        'view'
      ),
      flow: flowsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'flow')
        : call(zero),
      dataconnection: call(zero),
      database: call(zero),
      table: call(zero),
      virtualConnection: pConnsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'virtualConnection')
        : call(zero),
      virtualConnectionTable: pConnsEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'virtualConnectionTable')
        : call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: predictiveModelEnabled
        ? call(processContentCountRequestForCollectionSpaceContentType, 'predictiveModel')
        : call(zero),
    };
    return counts;
  } else if (isCatalogSpace(spaceInfo)) {
    const counts: SpaceInfoTypeContentCount<
      ExploreContentApi_ContentType,
      CallEffect
    > = {
      project: call(zero),
      workbook: call(zero),
      datasource: call(zero),
      dataRole: call(zero),
      lens: call(zero),
      view: call(zero),
      flow: call(zero),
      dataconnection: call(zero),
      database: catalogingOnSiteEnabled ? call(processContentCountRequestForCatalog, 'database') : call(zero),
      table: catalogingOnSiteEnabled ? call(processContentCountRequestForCatalog, 'table') : call(zero),
      virtualConnection: call(zero),
      virtualConnectionTable: call(zero),
      virtualConnectionColumn: call(zero),
      collection: call(zero),
      predictiveModel: call(zero),
    };
    return counts;
  } else if (isPublishedConnectionSpace(spaceInfo)) {
    const counts: Record<PublishedConnectionSubspaceName, CallEffect> = {
      scheduledTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'scheduledTasks',
      }),
      tables: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'tables',
      }),
      lineage: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lineage',
      }),
      runHistory: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'runHistory',
      }),
    };
    return counts;
  } else if (isPublishedConnectionTableSpace(spaceInfo)) {
    const counts: Record<PublishedConnectionTableSubspaceName, CallEffect> = {
      scheduledTasks: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'scheduledTasks',
      }),
      overview: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'overview',
      }),
      lineage: call(processContentCountRequestForSpaceInfo, {
        ...spaceInfo,
        subspaceName: 'lineage',
      }),
    };
    return counts;
  }
}

// exported for testing
export function* zero() {
  return 0;
}
